import { useImperativeHandle, useRef } from "react";
import { motion } from "framer-motion";
import useSwipePhysics from "@/lib/use_swipe_physics";
import {
  Briefcase,
  Wallet,
  CalendarDays,
  PawPrint,
  CigaretteOff,
  Cigarette,
  Users,
  ShieldCheck,
  Lock,
} from "lucide-react";
import { format } from "date-fns";
import BadgeVerified from "@/components/badges/badge_verified";
import { cn } from "@/lib/utils";

// Renter payloads can be LIMITED before a match: {first_name, budget_min,
// budget_max, about_short, locked: true} instead of the full profile.
function budgetLine(renter) {
  if (renter.budget_min || renter.budget_max) {
    const min = renter.budget_min ? `$${Number(renter.budget_min).toLocaleString()}` : "";
    const max = renter.budget_max ? `$${Number(renter.budget_max).toLocaleString()}` : "";
    if (min && max) return `Budget ${min} to ${max}/mo`;
    return `Budget ${min || `up to ${max}`}/mo`;
  }
  return `Budget $${Number(renter.budget || 0).toLocaleString()}/mo`;
}

function moveInLine(renter) {
  let from = "soon";
  try {
    if (renter.move_in) from = format(new Date(renter.move_in), "MMM d");
  } catch {
    from = renter.move_in || "soon";
  }
  const months = renter.lease_length_wanted || 12;
  return `Looking for: ${months} months from ${from}`;
}

function Chip({ icon: Icon, children, className }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full bg-night-surface/90 px-2.5 py-1 text-xs font-semibold text-night-text shadow-sm backdrop-blur",
        className
      )}
    >
      {Icon && <Icon className="h-3.5 w-3.5" />}
      {children}
    </span>
  );
}

// `edge` = full-bleed mode (the lister /swipe and /lister/browse pages):
// square corners on mobile so the card meets the screen edges Tinder style,
// extra bottom padding in the details so the floating action row never sits
// on top of the renter's bio. Desktop (lg) keeps the rounded card.
export default function RenterCard({ renter, active = true, onSwipe, onExpand, onExited, enterFrom = 0, mirrorX, style, edge = false, ref }) {
  // Shared Tinder-grade physics: 1:1 tracking, grab-point rotation pivot,
  // velocity + distance release, linear exit. Buttons use ref.swipe(action).
  const physics = useSwipePhysics({
    onSwipe: (action) => onSwipe?.(action),
    onExited,
    enabled: active,
    mirrorX,
    enterFrom,
  });
  const { likeOpacity, nopeOpacity, triggerSwipe, dragProps } = physics;
  const pressRef = useRef(null);

  useImperativeHandle(ref, () => ({ swipe: triggerSwipe }), [triggerSwipe]);

  // A real tap (not a drag) on the photo opens the renter profile expand.
  function handlePointerDown(e) {
    pressRef.current = { x: e.clientX, y: e.clientY, t: Date.now() };
  }
  function handlePhotoClick(e) {
    const press = pressRef.current;
    pressRef.current = null;
    if (!press) return;
    const moved = Math.hypot(e.clientX - press.x, e.clientY - press.y);
    if (moved < 8 && Date.now() - press.t < 400) onExpand?.();
  }

  return (
    <motion.div
      className={`absolute inset-0 select-none ${active ? "cursor-grab active:cursor-grabbing" : ""}`}
      style={{ ...physics.style, ...style }}
      {...dragProps}
    >
      <div className={`relative flex h-full w-full flex-col overflow-hidden bg-night-surface shadow-xl ring-1 ring-night-line ${edge ? "rounded-none lg:rounded-3xl" : "rounded-3xl"}`}>
        {/* Photo — tap opens the profile expand */}
        <div className="relative h-[46%] shrink-0 bg-night" onPointerDown={handlePointerDown} onClick={handlePhotoClick}>
          <img
            src={renter.photo}
            alt={renter.name}
            className="h-full w-full object-cover"
            draggable={false}
          />
          <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />

          {/* Stamps. WHY the edge offset: in full-bleed mode the listing
              selector pill and notices float over the top of the card, so the
              stamps drop below them; desktop and compact surfaces keep top-5. */}
          <motion.div
            style={{ opacity: likeOpacity }}
            className={`absolute left-5 -rotate-[18deg] rounded-lg border-4 border-brand-primary bg-white/80 px-3 py-1 text-2xl font-extrabold tracking-[5px] text-brand-primary ${edge ? "top-20 lg:top-5" : "top-5"}`}
          >
            LIKE
          </motion.div>
          <motion.div
            style={{ opacity: nopeOpacity }}
            className={`absolute right-5 rotate-[18deg] rounded-lg border-4 border-brand-ink bg-white/80 px-3 py-1 text-2xl font-extrabold tracking-[5px] text-brand-ink ${edge ? "top-20 lg:top-5" : "top-5"}`}
          >
            NOPE
          </motion.div>

          <div className="absolute bottom-3 left-4 right-4 flex items-end justify-between gap-2">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-2xl font-extrabold text-white drop-shadow">
                  {renter.name || renter.first_name}
                  <span className="ml-2 font-semibold text-white/85">{renter.age}</span>
                </h3>
                {renter.verified && <BadgeVerified />}
                {renter.kyc_verified && <BadgeVerified label="ID verified" />}
              </div>
              <p className="mt-0.5 flex items-center gap-1.5 text-sm font-medium text-white/90">
                {renter.locked ? (
                  <>
                    <Lock className="h-4 w-4" /> Unlocks on match
                  </>
                ) : (
                  <>
                    <Briefcase className="h-4 w-4" /> {renter.occupation}
                  </>
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Details */}
        <div className={`flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4 ${edge ? "pb-24 lg:pb-4" : ""}`}>
          <div className="flex flex-wrap gap-2">
            {renter.income_range && (
              <Chip icon={Wallet} className="bg-night-tint text-brand-lime">
                {renter.income_range}
              </Chip>
            )}
            <Chip icon={Wallet}>{budgetLine(renter)}</Chip>
            {renter.screening_complete && (
              <Chip icon={ShieldCheck} className="bg-night-tint text-brand-lime">
                Screening complete
              </Chip>
            )}
          </div>

          <p className="flex items-center gap-2 text-sm font-semibold text-night-text">
            <CalendarDays className="h-4 w-4 text-brand-primary" />
            {moveInLine(renter)}
          </p>

          <div className="flex flex-wrap gap-2 text-xs font-semibold text-night-sub">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-night-tint px-2.5 py-1">
              <PawPrint className="h-3.5 w-3.5" /> {renter.pets || "No pets"}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-night-tint px-2.5 py-1">
              {renter.smoking ? (
                <>
                  <Cigarette className="h-3.5 w-3.5" /> Smoker
                </>
              ) : (
                <>
                  <CigaretteOff className="h-3.5 w-3.5" /> Nonsmoker
                </>
              )}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-night-tint px-2.5 py-1">
              <Users className="h-3.5 w-3.5" /> {renter.household || "Single"}
            </span>
          </div>

          <p className="text-sm leading-relaxed text-night-sub">{renter.about || renter.about_short}</p>

          {renter.locked && (
            <p className="mt-auto flex items-center gap-2 rounded-2xl bg-night px-3 py-2.5 text-xs font-medium text-night-sub">
              <Lock className="h-3.5 w-3.5 shrink-0" />
              Occupation and references unlock when you both match
            </p>
          )}
        </div>
      </div>
    </motion.div>
  );
}
